<template>
    <div>
        <v-row>
            <v-col>
                <h4 class="text-h4">GL Account Statement</h4>
                <p class="text-subtitle-1">Statement of ledger account</p>
            </v-col>
        </v-row>
        <v-divider></v-divider>
        <v-form ref="form">
            <v-row class="my-2" align="center">
                <v-col cols="4">
                    <v-autocomplete
                        label="Account"
                        outlined
                        dense
                        hide-details
                        :value="form.account_id"
                        @input="formModel('account_id', $event)"
                        :items="accounts"
                        item-text="displayName"
                        item-value="id"
                        @update:search-input="searchAccountsAsync"
                        :rules="[isRequired]"
                    >
                    </v-autocomplete>
                </v-col>
                <v-col cols="2">
                    <DatePicker
                        label="From"
                        outlined
                        dense
                        hide-details
                        :value="form.from"
                        @input="formModel('from', $event)"
                        :rules=[isRequired]
                    />
                </v-col>
                <v-col cols="2">
                    <DatePicker
                        label="To"
                        outlined
                        dense
                        hide-details
                        :value="form.to"
                        @input="formModel('to', $event)"
                        :rules=[isRequired]
                    />
                </v-col>
                <v-col cols="2">
                    <v-btn block color="primary" @click="fetchAsync" :loading="loading">
                        <v-icon left>mdi-magnify</v-icon>
                        search
                    </v-btn>
                </v-col>
                <v-col cols="1">
                    <v-btn text color="primary" @click="printAsync" :loading="printLoading">
                        <v-icon left>mdi-printer</v-icon>
                        print
                    </v-btn>
                </v-col>
            </v-row>
        </v-form>
        <v-row no-gutters>
            <v-col cols="2" class="me-2">
                <v-text-field
                    label="Reference"
                    filled
                    single-line
                    dense
                    hide-details="auto"
                    :value="filter.reference"
                    @input="filterModel('reference', $event)"
                    prepend-inner-icon="mdi-filter"
                    clearable>
                </v-text-field>
            </v-col>
            <v-col  class="me-2">
                <v-text-field
                    label="Description"
                    filled
                    single-line
                    dense
                    hide-details="auto"
                    :value="filter.description"
                    @input="filterModel('description', $event)"
                    prepend-inner-icon="mdi-filter"
                    clearable>
                </v-text-field>
            </v-col>
            <v-col cols="2" class="me-2">
                <v-text-field
                    label="Min"
                    filled
                    single-line
                    dense
                    hide-details="auto"
                    :value="filter.min_amount"
                    @input="filterModel('min_amount', $event)"
                    prepend-inner-icon="mdi-filter"
                    clearable>
                </v-text-field>
            </v-col>
            <v-col cols="2" class="me-2">
                <v-text-field
                    label="Max"
                    filled
                    single-line
                    dense
                    hide-details="auto"
                    :value="filter.max_amount"
                    @input="filterModel('max_amount', $event)"
                    prepend-inner-icon="mdi-filter"
                    clearable>
                </v-text-field>
            </v-col>
            <v-col cols="2" class="me-2">
                <v-autocomplete
                    label="Dimension"
                    filled
                    single-line
                    dense
                    hide-details="auto"
                    :value="filter.dimension_id"
                    @input="filterModel('dimension_id', $event)"
                    :items="dimensions"
                    item-text="name"
                    item-value="id"
                    prepend-inner-icon="mdi-filter"
                    clearable
                    @update:search-input="searchDimensions">
                </v-autocomplete>
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col>
                <v-progress-linear indeterminate v-if="loading" />
                <v-simple-table dense>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Reference #</th>
                            <th>Reference Type</th>
                            <th>Description</th>
                            <th>Debit</th>
                            <th>Credit</th>
                            <th v-if="!isFiltered">Balance</th>
                            <th>Dimension</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="secondary" v-if="data && data.opening && data.period && data.account && data.account.currency">
                            <td colspan="6" class="font-weight-bold">
                                Opening Balance - {{data.period.start_at}}
                            </td>
                            <td v-if="!isFiltered" class="font-weight-bold">
                                <span
                                    :class="{'error--text': isNegative(data.opening.balance)}"
                                    >
                                    {{data.account.currency.abbreviation}} {{balance(data.opening.balance)}}
                                </span>
                            </td>
                            <td></td>
                        </tr>
                        <tr v-for="(d, i) in filteredData" :key="i" v-ripple @click="flashBack(d)">
                            <td style="min-width: 120px;">{{d.transaction_date}}</td>
                            <td>{{d.reference_code}}</td>
                            <td style="min-width: 160px;">{{d.transaction_type}}</td>
                            <td>{{d.narration}}</td>
                            <td>{{debit(d.amount)}}</td>
                            <td>{{credit(d.amount)}}</td>
                            <td class="font-weight-bold" v-if="!isFiltered">
                                <span :class="{'error--text': isNegative(d.balance)}">{{balance(d.balance)}}</span>
                            </td>
                            <td>{{d.dimension_name}}</td>
                        </tr>
                        <tr v-if="data && data.running && data.running.length == 0">
                            <td colspan="8" class="text-center">No Movement</td>
                        </tr>
                        <tr class="secondary" v-if="data && data.closing && data.period && data.account && data.account.currency">
                            <td colspan="6" class="font-weight-bold">
                                Closing Balance - {{data.period.end_at}}
                            </td>
                            <td v-if="!isFiltered" class="font-weight-bold">
                                <span
                                    :class="{'error--text': isNegative(data.closing.balance)}"
                                    >
                                    {{data.account.currency.abbreviation}} {{balance(data.closing.balance)}}
                                </span>
                            </td>
                            <td></td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import DatePicker from "./../../components/common/DatePicker.vue";
import { formatNumber } from 'accounting';
import { isRequired } from './../../utils/validations';
import { mapActions, mapGetters, mapState } from 'vuex';
import { getAccountStatement } from './../../services/ledger/reports';

export default {
    components: { DatePicker },
    data() {
        return {
            loading: false,
            printLoading: false
        };
    },
    computed: {
        ...mapState("accounting/statement", [
            "accounts", "dimensions", "filter", "form", "data"
        ]),
        ...mapGetters("accounting/statement", [
            "filteredData"
        ]),
        isFiltered() {
            if(!this.data && !this.data.running) return false;
            return this.filteredData.length != this.data.running.length;
        }
    },
    methods: {
        isRequired,
        ...mapActions("accounting/statement", [
            "searchAccountsAsync", "searchDimensions", "updateFilter", "updateForm", "fetch"
        ]),
        filterModel(key, value) {
            this.updateFilter({key, value})
        },
        formModel(key, value) {
            this.updateForm({key, value})
        },
        fetchAsync() {
            if(!this.$refs.form.validate()) return;
            this.loading = true;
            this.fetch().finally(() => this.loading = false);
        },
        printAsync() {
            if(!this.$refs.form.validate()) return;
            this.fetchAsync();
            this.printLoading = true;
            getAccountStatement(this.form, { "Accept": "text/html" }).then(response => {
                var printWindow = window.open('','','width=800,height=600');
                printWindow.document.write(response.data);
                printWindow.document.close();
                printWindow.focus();
                printWindow.print();
                printWindow.close();
            }).finally(() => {
                this.printLoading = false;
            });
        },
        debit(amount) {
            if(Math.sign(amount) == 1) return formatNumber(amount, 2);
            return null;
        },
        credit(amount) {
            if(Math.sign(amount) == -1) return formatNumber(Math.abs(amount), 2);
            return null;
        },
        balance(amount) {
            if(Math.sign(amount) == 1) return formatNumber(amount, 2);
            if(Math.sign(amount) == -1) return `(${formatNumber(Math.abs(amount), 2)})`;
        },
        isNegative(n) {
            return Math.sign(n) == -1;
        },
        navigate(name, id) {
            this.$router.push({name, params: {id}})
        },
        flashBack(d) {
            var prefix = d.reference_code.substring(0, 3);
            switch(d.reference_abreviation) {
                case "JV":
                    this.navigate("JournalEntryDisplay", d.reference_id);
                    break;
                case "PMT":
                    this.navigate("CustomerPaymentDisplay", d.reference_id);
                    break;
                case "INV":
                    this.navigate("CustomerInvoiceDisplay", d.reference_id);
                    break;
                case "CN":
                    if(prefix =="PCN"){
                        this.navigate("CreditNoteDisplay", d.reference_id);
                        break;
                    } else{
                        this.navigate("SalesCreditNoteDisplay", d.reference_id);
                        break;
                    }
                case "DN":
                    if(prefix =="PDN"){
                        this.navigate("DebitNoteDisplay", d.reference_id);
                        break;
                    } else{
                        this.navigate("SalesDebitNoteDisplay", d.reference_id);
                        break;
                    }
                case "PINV":
                    this.navigate("PurchaseInvoiceDisplay", d.reference_id);
                    break;
                case "PPMT":
                    this.navigate("PurchasePaymentDisplay", d.reference_id);
                    break;
                case "GIV":
                    this.navigate("GoodIssueDisplay", d.reference_code);
                    break;
                default:
                    alert("could not navigate to document")
            }
        }
    },
    mounted() {

    }
};
</script>

<style>
</style>